import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
} from '@chakra-ui/react';
import { MdClose } from 'react-icons/md';

import StrongModal from '../common/StrongModal';
import StrongInput from '../common/StrongInput';
import StrongTextarea from '../common/StrongTextarea';
import StrongImageDragDrop from '../common/StrongImageDragDrop';
import StrongSingleSelect from '../common/StrongSingleSelect';

const APPEAR_RULE_OPTIONS = [
  { label: 'First login only', value: 'first_open' },
  { label: 'Every open', value: 'every_open' },
];

interface Faq {
  question: string;
  answer: string;
}

interface ProgramTitle {
  _id: string;
  title: string;
}

interface EditProgramIntroModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: FormData) => void;
  selectedIntro?: any;
  programTitles: ProgramTitle[];
}

const EditProgramIntroModal = ({
  isOpen,
  onClose,
  onSubmit,
  selectedIntro,
  programTitles,
}: EditProgramIntroModalProps) => {
  const [title, setTitle] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [vimeoId, setVimeoId] = useState('');
  const [description, setDescription] = useState('');
  const [programId, setProgramId] = useState('');
  const [appearRule, setAppearRule] = useState('');
  const [faqs, setFaqs] = useState<Faq[]>([]);

  useEffect(() => {
    setTitle(selectedIntro?.title || '');
    setImage(null);
    setImageRemoved(false);
    setVimeoId(selectedIntro?.vimeoId || '');
    setDescription(selectedIntro?.description || '');
    setProgramId(selectedIntro?.programId?._id || selectedIntro?.programId || '');
    setAppearRule(selectedIntro?.appearRule || '');
    setFaqs(selectedIntro?.faqs?.map(({ question, answer }: Faq) => ({ question, answer })) || []);
  }, [selectedIntro, isOpen]);

  const addFaq = () => setFaqs((prev) => [...prev, { question: '', answer: '' }]);

  const updateFaq = (index: number, field: keyof Faq, value: string) => {
    setFaqs((prev) => prev.map((faq, i) => (i === index ? { ...faq, [field]: value } : faq)));
  };

  const removeFaq = (index: number) => {
    setFaqs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('vimeoId', vimeoId || '');
    formData.append('description', description || '');
    formData.append('programId', programId);
    formData.append('appearRule', appearRule);
    formData.append('faqs', JSON.stringify(faqs));
    if (image) formData.append('image', image);
    if (imageRemoved) formData.append('removeImage', 'true');
    onSubmit(formData);
  };

  const programOptions = programTitles.map((p) => ({ label: p.title, value: p._id }));
  const selectedProgram = programOptions.find((o) => o.value === programId) ?? undefined;
  const selectedRule = APPEAR_RULE_OPTIONS.find((o) => o.value === appearRule) ?? undefined;

  return (
    <StrongModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={selectedIntro ? 'Edit Program Intro' : 'Create Program Intro'}
      isSubmitDisabled={!title || !programId || !appearRule}
      body={
        <Tabs variant='enclosed' colorScheme='primary'>
          <TabList mb={4}>
            <Tab fontWeight='semibold'>Details</Tab>
            <Tab fontWeight='semibold'>
              FAQs{faqs.length > 0 ? ` (${faqs.length})` : ''}
            </Tab>
          </TabList>

          <TabPanels>
            {/* ── Details tab ── */}
            <TabPanel px={0} pt={0}>
              <VStack spacing={4} align='stretch'>
                <FormControl isRequired>
                  <FormLabel color='gray.600'>Title</FormLabel>
                  <StrongInput
                    placeholder='Enter title'
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color='gray.600'>Image (optional)</FormLabel>
                  <StrongImageDragDrop
                    onFileSelect={(file) => {
                      setImage(file);
                      if (file === null) setImageRemoved(true);
                    }}
                    defaultImageUrl={imageRemoved ? undefined : selectedIntro?.imageUrl}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color='gray.600'>Vimeo ID (optional)</FormLabel>
                  <StrongInput
                    placeholder='Paste Vimeo ID'
                    value={vimeoId}
                    onChange={(e) => setVimeoId(e.target.value)}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color='gray.600'>Description (optional)</FormLabel>
                  <StrongTextarea
                    placeholder='Enter description'
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color='gray.600'>Program</FormLabel>
                  <StrongSingleSelect
                    options={programOptions}
                    value={selectedProgram}
                    onSelectChange={setProgramId}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color='gray.600'>Appear Rule</FormLabel>
                  <StrongSingleSelect
                    options={APPEAR_RULE_OPTIONS}
                    value={selectedRule}
                    onSelectChange={setAppearRule}
                  />
                </FormControl>
              </VStack>
            </TabPanel>

            {/* ── FAQs tab ── */}
            <TabPanel px={0} pt={0}>
              <VStack spacing={3} align='stretch'>
                {faqs.length === 0 && (
                  <Box
                    p={4}
                    borderRadius='md'
                    borderWidth='1px'
                    borderColor='gray.200'
                    color='gray.400'
                    textAlign='center'
                    fontSize='sm'
                  >
                    No FAQs yet. Click "+ Add FAQ" to add one.
                  </Box>
                )}
                {faqs.map((faq, index) => (
                  <Box
                    key={index}
                    p={3}
                    borderRadius='md'
                    borderWidth='1px'
                    borderColor='gray.200'
                    bg='gray.50'
                  >
                    <Flex justifyContent='flex-end' mb={2}>
                      <IconButton
                        aria-label='Remove FAQ'
                        icon={<MdClose />}
                        size='xs'
                        variant='ghost'
                        colorScheme='red'
                        onClick={() => removeFaq(index)}
                      />
                    </Flex>
                    <VStack spacing={2}>
                      <StrongInput
                        placeholder='Question'
                        value={faq.question}
                        onChange={(e) => updateFaq(index, 'question', e.target.value)}
                      />
                      <StrongInput
                        placeholder='Answer'
                        value={faq.answer}
                        onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                      />
                    </VStack>
                  </Box>
                ))}
                <Button
                  size='sm'
                  variant='outline'
                  colorScheme='primary'
                  alignSelf='flex-start'
                  onClick={addFaq}
                >
                  + Add FAQ
                </Button>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      }
    />
  );
};

export default EditProgramIntroModal;
